import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  IoSettingsOutline,
  IoAddOutline,
  IoCubeOutline,
  IoClose,
} from "react-icons/io5";
import { MdOutlineDelete } from "react-icons/md";
import {
  getSubcategories,
  addBOQToInclusionFormData,
} from "../../../../../services/budgetService";
import { requiresLeadTime } from "../../../../../constants/boq";
import { InlineAddCategoryItemRow } from "./InlineAddCategoryItemRow";
import { InlineEditBoqRow } from "./InlineEditBoqRow";
import { BOQItemCard } from "./BOQItemCard";
import { BOQItem } from "./BOQItem";
import { isBoqUncovered } from "../../../isBoqUncovered";
import { getCurrentCurrencyLocale } from "../../../../../utils/CurrencyUtils";

const formatAed = (val) => {
  if (val == null || val === "") return "-";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return `${getCurrentCurrencyLocale()} ${n.toLocaleString("en-US")}`;
};

export const InclusionCard = ({
  inclusion,
  projectId,
  project,
  onOpenAddItemModal,
  onEditBoq,
  onDeleteBoq,
  onDeleteInclusion,
  onBudgetUpdated,
  disabled = false,
  defaultExpanded = false,
  showCostFields = true,
  viewOnly = false,
  isDisable = false,
  inlineEditingBoqId,
  inlineEditingBoqInclusion,
  onSaveInlineEditBoq,
  onCancelInlineEditBoq,
  inlineEditSaving,
  isMobile = false,
  isTablet = false,
  isSiteVisit = false,
  isSiteEstimatesRoute = false,
  isReview = false,
  isExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState({});
  const [pendingItems, setPendingItems] = useState({});
  const [savingItems, setSavingItems] = useState({});

  const milestoneId =
    inclusion?.id ??
    inclusion?.milestone_id ??
    inclusion?.milestoneId ??
    inclusion?.inclusion_id;
  const inclusionKey = milestoneId ?? inclusion?.name;

  const editingInclusionId =
    inlineEditingBoqInclusion?.id ??
    inlineEditingBoqInclusion?.milestone_id ??
    inlineEditingBoqInclusion?.milestoneId ??
    inlineEditingBoqInclusion?.inclusion_id;

  const fetchSubcategories = useCallback(async () => {
    if (isExpanded) return;
    if (!inclusion?.name) return;
    try {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionKey]: true }));
      const data = await getSubcategories(inclusion.name, project?.project_type || "");
      if (data?.success && data.subcategories) {
        const options = data.subcategories.map((item) => ({
          label: item.subcategory_name,
          value: item.subcategory_name,
        }));
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionKey]: options }));
      } else {
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionKey]: [] }));
      }
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubcategoriesMap((prev) => ({ ...prev, [inclusionKey]: [] }));
    } finally {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionKey]: false }));
    }
  }, [inclusion?.name, inclusionKey, project?.project_type, isExpanded]);

  const toggleExpand = useCallback(async () => {
    const willExpand = !expanded;
    setExpanded(willExpand);
    if (isExpanded) return;
    if (willExpand && subcategoriesMap[inclusionKey] === undefined) {
      await fetchSubcategories();
    }
  }, [expanded, inclusionKey, subcategoriesMap, fetchSubcategories, isExpanded]);

  const getNextItemNameForSubcategory = (inclusionId, subcategoryValue) => {
    const escaped = String(subcategoryValue).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`^${escaped} \\d+$`);
    const sameBase = Object.values(pendingItems).filter(
      (item) =>
        item.inclusionId === inclusionId &&
        (item.item_name === subcategoryValue || regex.test(item.item_name)),
    );
    return sameBase.length === 0 ? subcategoryValue : `${subcategoryValue} ${sameBase.length}`;
  };

  const handleSubcategoryClick = (inclusionId, subcategory) => {
    const uniqueKey = `${inclusionId}_${subcategory.value}_${Date.now()}`;
    const itemName = getNextItemNameForSubcategory(inclusionId, subcategory.value);
    setPendingItems((prev) => ({
      ...prev,
      [uniqueKey]: {
        id: uniqueKey,
        inclusionId,
        item_name: itemName,
        subcategory: subcategory.value,
        boq_type: "Materials",
        quantity: "",
        days: "",
        material_unit: null,
        supply_apply: null,
        specification: "",
        room_id: null,
        lead_time: "",
      },
    }));
  };

  const handleUpdatePendingItem = (key, updates) => {
    setPendingItems((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));
  };

  const handleCancelPendingItem = (key) => {
    setPendingItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSavePendingItem = async (key, inclusionObj) => {
    const pendingItem = pendingItems[key];
    if (!pendingItem) return;
    if (!pendingItem.item_name) {
      toast.error("Item name is required");
      return;
    }
    if (pendingItem.boq_type === "Materials") {
      const qty = pendingItem.quantity || "";
      if (qty === "" || isNaN(qty) || parseInt(qty) < 0) {
        toast.error("Quantity is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Activities") {
      const d = pendingItem.days || "";
      if (d === "" || isNaN(d) || parseInt(d) < 0) {
        toast.error("Days is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Materials" && !pendingItem.material_unit) {
      toast.error("Material Unit is required");
      return;
    }
    if (!pendingItem.supply_apply) {
      toast.error("Supply/Apply is required");
      return;
    }
    // Lead Time mandatory when Supply / Supply and Apply (reno-web rule).
    if (requiresLeadTime(pendingItem.supply_apply)) {
      const lt = String(pendingItem.lead_time ?? "").trim();
      if (lt === "") {
        toast.error("Please enter Lead Time (days)");
        return;
      }
      if (!/^\d+$/.test(lt)) {
        toast.error("Lead Time must be a positive integer");
        return;
      }
    }
    if (!pendingItem.specification) {
      toast.error("Specification is required");
      return;
    }
    if (!pendingItem.room_id) {
      toast.error("Please select a room");
      return;
    }

    setSavingItems((prev) => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("name", pendingItem.item_name);
      formData.append("type", pendingItem.boq_type);
      formData.append("subcategory", pendingItem.subcategory);
      formData.append("supply_type", pendingItem.supply_apply);
      formData.append("specification", pendingItem.specification);
      formData.append(
        "material_unit",
        pendingItem.boq_type === "Materials" ? pendingItem.material_unit ?? "" : "",
      );
      if (pendingItem.boq_type === "Materials") {
        formData.append("quantity", pendingItem.quantity || "");
        formData.append("days", "");
      } else {
        formData.append("days", pendingItem.days || "");
        formData.append("quantity", "");
      }
      formData.append("room_id", pendingItem.room_id);
      formData.append("inclusion_name", inclusionObj.name);
      if (
        requiresLeadTime(pendingItem.supply_apply) &&
        pendingItem.lead_time != null &&
        String(pendingItem.lead_time).trim() !== ""
      ) {
        formData.append("lead_time", String(pendingItem.lead_time).trim());
      }

      // No inline image attachment — image is uploaded after save from
      // the read-only BOQItem/BOQItemCard.
      const data = await addBOQToInclusionFormData(projectId, inclusionObj.id, formData);
      if (data?.success !== false) {
        toast.success(`Successfully added ${pendingItem.item_name} to ${inclusionObj.name}`);
        handleCancelPendingItem(key);
        onBudgetUpdated?.();
      } else {
        toast.error("Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    } finally {
      setSavingItems((prev) => ({ ...prev, [key]: false }));
    }
  };

  const getPendingItemsForInclusion = (inclusionId) =>
    Object.entries(pendingItems)
      .filter(([key]) => key.startsWith(`${inclusionId}_`))
      .map(([key, item]) => ({ key, ...item }));

  const subcategories = subcategoriesMap[inclusionKey] || [];
  const isLoadingSubcats = loadingSubcategories[inclusionKey];
  const inclusionPendingItems = getPendingItemsForInclusion(inclusionKey);

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: 24, borderBottom: "1px solid #e5e7eb" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <IoSettingsOutline style={{ width: 20, height: 20, color: "#4b5563" }} />
            <div>
              <h4 style={{ fontSize: 18, fontWeight: 500, color: "#0D0D0E", margin: 0 }}>
                {inclusion.name}
              </h4>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 14, color: "#4b5563" }}>
                  {inclusion.boq_count} items
                </span>
                <span
                  style={{
                    padding: "2px 8px",
                    fontSize: 12,
                    fontWeight: 500,
                    background: "#ffedd5",
                    color: "#9a3412",
                    borderRadius: 999,
                  }}
                >
                  {formatAed(inclusion.inclusion_amount)}
                </span>
              </div>
              {inclusion.rooms?.length > 0 && (
                <div style={{ fontSize: 14, color: "#4b5563", marginTop: 4 }}>
                  Used in: {inclusion.rooms.map((r) => r.name).join(", ")}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* {!viewOnly && !isDisable && onDeleteInclusion && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteInclusion(inclusion);
                }}
                style={{
                  padding: 8,
                  color: "#9ca3af",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                title="Delete inclusion"
              >
                <MdOutlineDelete style={{ width: 20, height: 20 }} />
              </button>
            )} */}
            <button
              onClick={toggleExpand}
              style={{
                padding: 8,
                color: "#9ca3af",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <svg
                style={{
                  width: 20,
                  height: 20,
                  transform: expanded ? "none" : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 15l6-6 6 6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Rooms and BOQs */}
      {expanded && (
        <div>
          {(inclusion.rooms || []).map((room) => (
            <div
              key={room.id || room.room_id}
              style={{ padding: "24px 24px 24px 48px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <IoCubeOutline style={{ width: 16, height: 16, color: "#4b5563" }} />
                <h5 style={{ fontWeight: 500, color: "#0D0D0E", margin: 0 }}>{room.name}</h5>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {(room.boqs || []).map((boq) => {
                  const uncovered = isBoqUncovered(boq);
                  return inlineEditingBoqId === boq.id && editingInclusionId === milestoneId ? (
                    <InlineEditBoqRow
                      key={`edit-${boq.id}`}
                      boq={{ ...boq, room_name: room.name }}
                      inclusion={inclusion}
                      projectId={projectId}
                      onSave={() => onSaveInlineEditBoq && onSaveInlineEditBoq()}
                      onCancel={onCancelInlineEditBoq}
                      isSaving={inlineEditSaving}
                      showCostFields={showCostFields}
                      showUnitCostInEdit={false}
                      onBudgetUpdated={onBudgetUpdated}
                      isSiteVisit={isSiteVisit}
                      isSiteEstimatesRoute={isSiteEstimatesRoute}
                      isUncovered={uncovered && !isSiteVisit}
                    />
                  ) : isMobile ? (
                    <BOQItemCard
                      key={boq.id}
                      boq={{ ...boq, room_name: room.name }}
                      projectId={projectId}
                      inclusionId={milestoneId}
                      showCostFields={showCostFields}
                      showUnitCostInput={!isSiteVisit && !viewOnly}
                      isSiteVisit={isSiteVisit}
                      isSiteEstimatesRoute={isSiteEstimatesRoute}
                      onBudgetUpdated={onBudgetUpdated}
                      onEdit={viewOnly ? undefined : (b) => onEditBoq && onEditBoq(b, inclusion)}
                      onDelete={viewOnly ? undefined : (b) => onDeleteBoq && onDeleteBoq(b, inclusion.id)}
                      disabled={disabled}
                      isDisable={isDisable}
                      isUncovered={uncovered && !isSiteVisit}
                    />
                  ) : (
                    <BOQItem
                      key={boq.id}
                      boq={{ ...boq, room_name: room.name }}
                      showCostFields={showCostFields}
                      onEdit={viewOnly ? undefined : (b) => onEditBoq && onEditBoq(b, inclusion)}
                      onDelete={viewOnly ? undefined : (b) => onDeleteBoq && onDeleteBoq(b, inclusion.id)}
                      onBudgetUpdated={onBudgetUpdated}
                      disabled={disabled}
                      isDisable={isDisable}
                      isUncovered={uncovered && !isSiteVisit}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Desktop/tablet inline add */}
          {!viewOnly && !isDisable && !isMobile && (
            <div style={{ padding: 24, background: "#f9fafb", display: "flex", flexDirection: "column", gap: 16 }}>
              {inclusionPendingItems.map((item) => (
                <InlineAddCategoryItemRow
                  key={item.key}
                  pendingItem={item}
                  onUpdate={(updates) => handleUpdatePendingItem(item.key, updates)}
                  onSave={() => handleSavePendingItem(item.key, inclusion)}
                  onCancel={() => handleCancelPendingItem(item.key)}
                  isSaving={savingItems[item.key]}
                  selectableRooms={inclusion.rooms}
                />
              ))}

              {/* <div>
                {!isExpanded && (
                  <div style={{ fontSize: 14, color: "#4b5563", marginBottom: 8 }}>Add items:</div>
                )}
                {isLoadingSubcats ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "#6b7280" }}>
                    <span>Loading subcategories...</span>
                  </div>
                ) : subcategories.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {subcategories.map((subcat) => (
                      <button
                        key={subcat.value}
                        onClick={() => handleSubcategoryClick(inclusionKey, subcat)}
                        style={{
                          padding: "6px 12px",
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#4b5563",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          cursor: "pointer",
                        }}
                      >
                        <IoAddOutline style={{ width: 12, height: 12 }} />
                        {subcat.label}
                      </button>
                    ))}
                  </div>
                ) : !isExpanded ? (
                  <div style={{ fontSize: 14, color: "#9ca3af" }}>No subcategories available</div>
                ) : null}
              </div> */}
            </div>
          )}

          {/* Mobile add button */}
          {/* {!viewOnly && !isDisable && onOpenAddItemModal && isMobile && (
            <div style={{ padding: 24, background: "#f9fafb" }}>
              <button
                onClick={() => onOpenAddItemModal(inclusion)}
                style={{
                  width: "100%",
                  padding: "8px 16px",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#2563eb",
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  cursor: "pointer",
                }}
              >
                <IoAddOutline style={{ width: 16, height: 16, display: "inline", marginRight: 8, verticalAlign: "middle" }} />
                Add Item to {inclusion.name}
              </button>
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default InclusionCard;
